import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from "@mui/material";
import { adminApi } from "../api/client";

interface ReportsPayload {
  quotationStatus: Array<any>;
  conversion: any;
  revenue: Array<any>;
  upcomingEvents: Array<any>;
  paymentMilestones: Array<any>;
  customers: Array<any>;
  itemAnalytics: Array<any>;
}

function PaginatedReportTable({ title, rows }: { title: string; rows: Array<Record<string, any>> }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const columns = rows.length ? Object.keys(rows[0]) : [];
  const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  useEffect(() => {
    setPage(0);
  }, [rows]);

  return (
    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(118, 93, 68, 0.12)", background: "rgba(255, 250, 244, 0.96)" }}>
      <Typography variant="h6" sx={{ p: 2.25, pb: 0.5, fontWeight: 800 }}>
        {title}
      </Typography>
      {!rows.length ? (
        <Box sx={{ px: 2.25, pb: 2.5, pt: 1.5 }}>
          <Typography sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>
            No data found.
          </Typography>
        </Box>
      ) : null}
      <Table size="small">
        {rows.length ? (
          <>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column} sx={{ fontWeight: 700 }}>
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.map((row, index) => (
                <TableRow key={page * rowsPerPage + index}>
                  {columns.map((column) => (
                    <TableCell key={column}>{String(row[column] ?? "-")}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </>
        ) : null}
      </Table>
      {rows.length ? (
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
        />
      ) : null}
    </TableContainer>
  );
}

export default function ReportsPage() {
  const [data, setData] = useState<ReportsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [
          quotationStatus,
          conversion,
          revenue,
          upcomingEvents,
          paymentMilestones,
          customers,
          itemAnalytics
        ] = await Promise.all([
          adminApi.get<Array<any>>("/reports/quotations/status-summary"),
          adminApi.get<any>("/reports/quotations/conversion"),
          adminApi.get<Array<any>>("/reports/revenue/summary"),
          adminApi.get<Array<any>>("/reports/events/upcoming"),
          adminApi.get<Array<any>>("/reports/payments/milestones"),
          adminApi.get<Array<any>>("/reports/customers/summary"),
          adminApi.get<Array<any>>("/reports/items/analytics")
        ]);

        setData({
          quotationStatus,
          conversion,
          revenue,
          upcomingEvents,
          paymentMilestones,
          customers,
          itemAnalytics
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load reports");
      }
    };

    void load();
  }, []);

  const renderTable = (title: string, rows: Array<Record<string, any>>) => {
    return <PaginatedReportTable title={title} rows={rows} />;
  };

  return (
    <Stack spacing={3}>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid rgba(118, 93, 68, 0.12)", background: "rgba(255, 250, 244, 0.94)" }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
          Reports
        </Typography>
        <Typography sx={{ color: "text.secondary" }}>
          Business insights across quotation pipeline, customer conversions, revenue, schedules, and service demand.
        </Typography>
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, border: "1px solid rgba(118, 93, 68, 0.12)", background: "rgba(255, 250, 244, 0.94)" }}>
            <Typography variant="overline">Total Quotes</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, mt: 0.4, fontSize: { xs: "2rem", md: "2.25rem", xl: "3rem" }, overflowWrap: "anywhere" }}>
              {data?.conversion?.total_quotes || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, border: "1px solid rgba(118, 93, 68, 0.12)", background: "rgba(255, 250, 244, 0.94)" }}>
            <Typography variant="overline">Granted Quotes</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, mt: 0.4, fontSize: { xs: "2rem", md: "2.25rem", xl: "3rem" }, overflowWrap: "anywhere" }}>
              {data?.conversion?.granted_quotes || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, border: "1px solid rgba(118, 93, 68, 0.12)", background: "rgba(255, 250, 244, 0.94)" }}>
            <Typography variant="overline">Grant Rate</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, mt: 0.4, fontSize: { xs: "2rem", md: "2.25rem", xl: "3rem" }, overflowWrap: "anywhere" }}>
              {data?.conversion?.grant_rate_percent || 0}%
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {renderTable("Quotation Status Summary", data?.quotationStatus || [])}
      {renderTable("Revenue Summary", data?.revenue || [])}
      {renderTable("Upcoming Events", data?.upcomingEvents || [])}
      {renderTable("Payment Milestones", data?.paymentMilestones || [])}
      {renderTable("Customer Summary", data?.customers || [])}
      {renderTable("Item Analytics", data?.itemAnalytics || [])}
    </Stack>
  );
}
